import { SkyCard, SkyInput, SkyText } from "@skypointcloud/skypoint-ui";
import { useId, type Ref } from "react";
import type { ScheduleDraft } from "./scheduleForm";

/**
 * Schedule name: the first of the editor's three cards (round 6, S4: Schedule name, What runs,
 * When). The field a person fills in without thinking comes first; what runs is decided before
 * when, because the time depends on what it moves (after the source's nightly close, or after an
 * external feed lands).
 */

export interface ScheduleNameSectionProps {
  draft: ScheduleDraft;
  onChange: (draft: ScheduleDraft) => void;
  fieldErrors?: Record<string, string>;
  disabled?: boolean;
  /** The card, so a failed save can scroll to it and focus it. */
  ref?: Ref<HTMLElement>;
}

export function ScheduleNameSection({
  draft,
  onChange,
  fieldErrors = {},
  disabled = false,
  ref,
}: ScheduleNameSectionProps) {
  const headingId = useId();
  return (
    <section
      ref={ref}
      tabIndex={-1}
      aria-labelledby={headingId}
      data-testid="schedule-name-section"
      className="rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <SkyCard
        appearance="outline"
        header={{
          title: (
            <SkyText as="h2" id={headingId} size={400} weight="semibold">
              Schedule name
            </SkyText>
          ),
        }}
      >
        <SkyInput
          label="Schedule name"
          required
          value={draft.name}
          onChange={(event) => onChange({ ...draft, name: event.target.value })}
          maxLength={200}
          hint="Name it after what it does, so the runs list reads plainly."
          disabled={disabled}
          validationState={fieldErrors.name ? "error" : "none"}
          validationMessage={fieldErrors.name}
          testId="schedule-name"
        />
      </SkyCard>
    </section>
  );
}
